import { useEffect } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowLeft, GraduationCap, FileText, Sparkles } from "lucide-react";
import { useProfile } from "@/hooks/use-profile";
import { useAllModuleStatuses } from "@/hooks/use-module-status";
import { useRolePerspective } from "@/hooks/use-role-perspective";

export default function TeacherDashboard() {
  const [, setLocation] = useLocation();
  const { user, profile, loading } = useProfile();
  const { effectiveRole } = useRolePerspective(profile?.id, profile?.role);
  const { statuses, loading: statusesLoading } = useAllModuleStatuses();

  useEffect(() => {
    if (!loading && user && effectiveRole === 'student') {
      setLocation("/");
    }
  }, [effectiveRole, loading, setLocation, user]);

  if (loading || statusesLoading) {
    return <div className="min-h-screen flex items-center justify-center p-4">Loading...</div>;
  }

  const isWorksheetActive = statuses.worksheet === 'active' || effectiveRole === 'admin';
  const isStarterActive = statuses.starter === 'active' || effectiveRole === 'admin';
  const isQuestionBanksActive = statuses.my_question_banks === 'active' || statuses.my_question_banks === undefined || effectiveRole === 'admin';

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto space-y-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setLocation("/")}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="p-3 bg-primary/10 rounded-lg">
                <GraduationCap className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h1 className="text-3xl font-bold font-serif">Teacher Dashboard</h1>
                <p className="text-muted-foreground mt-1">
                  Create custom worksheets and classroom starters
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {statuses.worksheet !== 'hidden' && (
              <Card
                className={`p-6 transition-all border-2 ${isWorksheetActive ? 'cursor-pointer hover:shadow-lg hover:border-primary/50 group' : 'opacity-60 cursor-not-allowed bg-muted/30'}`}
                onClick={() => {
                  if (isWorksheetActive) setLocation("/teacher/worksheet");
                }}
              >
                <div className="flex flex-col gap-4">
                  <div className={`p-3 rounded-lg w-fit transition-colors ${isWorksheetActive ? 'bg-primary/10 group-hover:bg-primary/20' : 'bg-muted pb-0'}`}>
                    <FileText className={`h-8 w-8 ${isWorksheetActive ? 'text-primary' : 'text-muted-foreground'}`} />
                  </div>
                  <div className="flex-1 w-full">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-xl font-bold">Worksheet Generator</h3>
                      {!isWorksheetActive && (
                        <span className="text-xs font-semibold px-2 py-1 bg-amber-500/10 text-amber-600 dark:text-amber-400 rounded-md uppercase tracking-wider">
                          {statuses.worksheet === 'coming_soon' ? 'Coming Soon' : 'Maintenance'}
                        </span>
                      )}
                    </div>
                    <p className="text-muted-foreground">
                      Create printable worksheets with multiple questions, answer keys, and explanations. Perfect for homework or in-class practice.
                    </p>
                  </div>
                </div>
              </Card>
            )}

            {statuses.starter !== 'hidden' && (
              <Card
                className={`p-6 transition-all border-2 ${isStarterActive ? 'cursor-pointer hover:shadow-lg hover:border-primary/50 group' : 'opacity-60 cursor-not-allowed bg-muted/30'}`}
                onClick={() => {
                  if (isStarterActive) setLocation("/teacher/starter");
                }}
              >
                <div className="flex flex-col gap-4">
                  <div className={`p-3 rounded-lg w-fit transition-colors ${isStarterActive ? 'bg-primary/10 group-hover:bg-primary/20' : 'bg-muted pb-0'}`}>
                    <Sparkles className={`h-8 w-8 ${isStarterActive ? 'text-primary' : 'text-muted-foreground'}`} />
                  </div>
                  <div className="flex-1 w-full">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-xl font-bold">Classroom Starters</h3>
                      {!isStarterActive && (
                        <span className="text-xs font-semibold px-2 py-1 bg-amber-500/10 text-amber-600 dark:text-amber-400 rounded-md uppercase tracking-wider">
                          {statuses.starter === 'coming_soon' ? 'Coming Soon' : 'Maintenance'}
                        </span>
                      )}
                    </div>
                    <p className="text-muted-foreground">
                      Interactive full-screen mode for displaying questions on the board. Features revealable answers and difficulty selection.
                    </p>
                  </div>
                </div>
              </Card>
            )}

            {statuses.my_question_banks !== 'hidden' && (
              <Card
                className={`p-6 transition-all border-2 ${isQuestionBanksActive && user ? 'cursor-pointer hover:shadow-lg hover:border-primary/50 group' : 'opacity-60 cursor-not-allowed bg-muted/30'}`}
                onClick={() => {
                  if (isQuestionBanksActive && user) {
                    setLocation("/teacher/banks");
                  }
                }}
              >
                <div className="flex flex-col gap-4">
                  <div className={`p-3 rounded-lg transition-colors w-fit ${isQuestionBanksActive && user ? 'bg-primary/10 group-hover:bg-primary/20' : 'bg-muted pb-0'}`}>
                    <GraduationCap className={`h-8 w-8 ${isQuestionBanksActive && user ? 'text-primary' : 'text-muted-foreground'}`} />
                  </div>
                  <div className="flex-1 w-full">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-xl font-bold">My Question Banks</h3>
                      {!user ? (
                        <span className="text-xs font-semibold px-2 py-1 bg-primary/10 text-primary rounded-md uppercase tracking-wider">
                          Login Required
                        </span>
                      ) : !isQuestionBanksActive && (
                        <span className="text-xs font-semibold px-2 py-1 bg-amber-500/10 text-amber-600 dark:text-amber-400 rounded-md uppercase tracking-wider">
                          {statuses.my_question_banks === 'coming_soon' ? 'Coming Soon' : 'Maintenance'}
                        </span>
                      )}
                    </div>
                    <p className="text-muted-foreground">
                      Create and manage your own custom topic banks for Starters and Worksheets.
                    </p>
                  </div>
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
